import { formatTournamentDateTime } from '../utils/tournamentTime';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSocketUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';
import Loading from '../components/Loading';
const getAdvertisedRewardPool = (t) => Number(getRankRewards(t).find(r => Number(r.rank) === 1)?.amount || 0);
const getRankRewards = (t) => Array.isArray(t.rank_rewards) ? t.rank_rewards : [];

import { Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import PlatformConfirmModal from '../components/PlatformConfirmModal';

const SOCKET_URL = getSocketUrl();
const TURN_DURATION = 10;
const MISS_LIMIT = 3;

export default function TournamentsPage() {
  const navigate = useNavigate();
  const isLoggedIn = !!getAccessToken();
  const token = getAccessToken();

  // Tournament list state
  const [tournaments, setTournaments] = useState([]);
  const [myTournaments, setMyTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  // Detail/Play state
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [detailTab, setDetailTab] = useState('leaderboard');
  const [joining, setJoining] = useState(false);
  const [playsRemaining, setPlaysRemaining] = useState(null);
  const [isJoined, setIsJoined] = useState(false);

  // Game play state
  const [gameMode, setGameMode] = useState(null); // null | 'playing' | 'finished'
  const [playId, setPlayId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [diceResult, setDiceResult] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [turnTimer, setTurnTimer] = useState(TURN_DURATION);
  const [gameResult, setGameResult] = useState(null);
  const [waitingForShape, setWaitingForShape] = useState(null);
  const [showResignConfirm, setShowResignConfirm] = useState(false);

  // Winner modal
  const [winnerModal, setWinnerModal] = useState(false);
  const [winnerData, setWinnerData] = useState(null);

  const socketRef = useRef(null);
  const timerRef = useRef(null);

  // ========================================================
  // LOAD TOURNAMENTS
  // ========================================================
  useEffect(() => {
    loadTournaments();
  }, [filter]);

  const loadTournaments = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await api.tournaments.getAll(params);
      setTournaments(res.data.tournaments || res.data.data || []);

      if (isLoggedIn) {
        try {
          const myRes = await api.tournaments.getMyTournaments();
          const mine = myRes.data.tournaments || myRes.data.data || [];
          setMyTournaments(mine);
          setTournaments(prev => {
            const cancelledMine = mine.filter(mt => mt.status === 'cancelled' && !prev.some(t => t.id === mt.id));
            return cancelledMine.length ? [...prev, ...cancelledMine] : prev;
          });
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // VIEW TOURNAMENT DETAIL
  // ========================================================
  const viewTournament = async (tournament) => {
    setSelectedTournament(tournament);
    setDetailTab('leaderboard');
    setLeaderboard([]);
    setParticipants([]);
    setPayouts([]);
    setPlaysRemaining(null);
    setIsJoined(false);

    // Check if joined
    if (isLoggedIn) {
      const myT = myTournaments.find(mt => mt.id === tournament.id);
      if (myT) {
        setIsJoined(true);
        setPlaysRemaining(myT.playsRemaining !== undefined ? myT.playsRemaining :
          (tournament.max_plays_per_player - (myT.plays_used || 0)));
      }
    }

    // Load leaderboard
    try {
      const lbRes = await api.tournaments.getLeaderboard(tournament.id);
      setLeaderboard(lbRes.data.leaderboard || lbRes.data.data || []);
      if (lbRes.data.yourPlaysRemaining !== undefined) {
        setPlaysRemaining(lbRes.data.yourPlaysRemaining);
      }
    } catch (e) { /* ignore */ }

    // Load participants if admin or detail view
    try {
      // Just use the leaderboard data for now
    } catch (e) { /* ignore */ }

    // Check if completed for payouts
    if (tournament.status === 'completed') {
      try {
        const payoutRes = await api.tournaments.getPayouts(tournament.id);
        const myPayouts = payoutRes.data.payouts || [];
        setPayouts(myPayouts);
        // The backend now self-scopes payout data from the authenticated user.
        const myPayout = myPayouts[0] || null;
        if (myPayout) {
          setWinnerData(myPayout);
          setWinnerModal(true);
        }
      } catch (e) { /* ignore */ }
    }
  };

  const backToList = () => {
    setSelectedTournament(null);
    setGameMode(null);
    setPlayId(null);
    setGameState(null);
    setGameResult(null);
    loadTournaments();
  };

  // ========================================================
  // JOIN TOURNAMENT
  // ========================================================
  const handleJoin = async () => {
    if (!isLoggedIn) return navigate('/login');
    if (!selectedTournament) return;

    setJoining(true);
    try {
      const res = await api.tournaments.join(selectedTournament.id);
      setIsJoined(true);
      setPlaysRemaining(res.data.maxPlays || selectedTournament.max_plays_per_player || 50);

      // Update my tournaments list
      setMyTournaments(prev => {
        const exists = prev.find(mt => mt.id === selectedTournament.id);
        if (exists) return prev;
        return [...prev, { id: selectedTournament.id, plays_used: 0 }];
      });
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to join tournament');
    } finally {
      setJoining(false);
    }
  };

  // ========================================================
  // START PLAY
  // ========================================================
  const handleStartPlay = async () => {
    if (!selectedTournament || !isLoggedIn) return;

    // Whot Solo Tournament has its own isolated gameplay route.
    // Let TournamentPlay own the single start-play request so this page
    // cannot create a play and then attach it to the normal /whot socket.
    if (selectedTournament.game_type === 'whot') {
      navigate(`/tournaments/${selectedTournament.id}/play`);
      return;
    }

    try {
      const res = await api.tournaments.startPlay(selectedTournament.id);
      const data = res.data;
      setPlayId(data.playId);
      setPlaysRemaining(data.playsRemaining);
      setGameMode('connecting');
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to start play');
    }
  };

  // ========================================================
  // SOCKET CONNECTION
  // ========================================================
  useEffect(() => {
    if (!playId || !selectedTournament || gameMode !== 'connecting') return;

    const gameType = selectedTournament.game_type || selectedTournament.gameType;
    const namespace = '/whot';

    // Clean up any existing connection
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(`${SOCKET_URL}${namespace}`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Tournament socket connected to', namespace);
      socket.emit('startTournamentPlay', {
        tournamentId: selectedTournament.id,
        playId,
      });
    });

    socket.on('gameState', (state) => {
      setGameState(state);
      setDiceResult(state.diceResult || null);
      setValidMoves([]);
      setGameMode('playing');
      resetTimer();
    });

    socket.on('diceRolled', (data) => {
      setDiceResult(data.diceResult);
      setValidMoves(data.validMoves || []);
      if (data.turnEnded) {
        setTimeout(() => {
          setDiceResult(null);
          setValidMoves([]);
        }, 1500);
      }
    });

    socket.on('tokenMoved', () => {
      setDiceResult(null);
      setValidMoves([]);
    });

    socket.on('cardsDrawn', () => {
      // Card drawn, state will update
    });

    socket.on('shapeSelectionRequired', (data) => {
      setWaitingForShape(data);
    });

    socket.on('tournamentPlayEnded', (data) => {
      if (data) setGameResult(data);
      setGameMode('finished');
      clearTimer();
      refreshLeaderboard();
    });

    socket.on('forfeited', (data) => {
      setGameResult({ ...data, status: 'forfeited' });
      setGameMode('finished');
      clearTimer();
    });

    socket.on('timeUp', (data) => {
      setGameResult({ ...data, status: 'completed' });
      setGameMode('finished');
      clearTimer();
    });

    socket.on('missedTurn', (data) => {
      // Show missed turn warning (handled via gameState)
    });

    socket.on('error', (data) => {
      alert(data.message || 'An error occurred');
    });

    socket.on('disconnect', () => {
      console.log('Tournament socket disconnected');
    });

    return () => {
      clearTimer();
      socket.disconnect();
    };
  }, [playId]);

  // ========================================================
  // TIMER
  // ========================================================
  const resetTimer = useCallback(() => {
    clearTimer();
    setTurnTimer(TURN_DURATION);
    timerRef.current = setInterval(() => {
      setTurnTimer(prev => {
        if (prev <= 1) {
          if (socketRef.current && playId) {
            socketRef.current.emit('tournamentTurnTimeout', { playId });
          }
          return TURN_DURATION;
        }
        return prev - 1;
      });
    }, 1000);
  }, [playId]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // ========================================================
  // REFRESH LEADERBOARD
  // ========================================================
  const refreshLeaderboard = async () => {
    if (!selectedTournament) return;
    try {
      const lbRes = await api.tournaments.getLeaderboard(selectedTournament.id);
      setLeaderboard(lbRes.data.leaderboard || lbRes.data.data || []);
      if (lbRes.data.yourPlaysRemaining !== undefined) {
        setPlaysRemaining(lbRes.data.yourPlaysRemaining);
      }
    } catch (e) { /* ignore */ }
  };

  // ========================================================
  // GAME ACTIONS
  // ========================================================
  const handleRollDice = () => {
    if (!socketRef.current || !playId) return;
    socketRef.current.emit('tournamentAction', { playId, action: 'rollDice' });
  };

  const handleMoveToken = (tokenIndex) => {
    if (!socketRef.current || !playId) return;
    socketRef.current.emit('tournamentAction', {
      playId,
      action: { type: 'moveToken', tokenIndex },
    });
  };

  const handlePlayCard = (cardId, calledShape) => {
    if (!socketRef.current || !playId) return;
    socketRef.current.emit('tournamentAction', {
      playId,
      action: { type: 'playCard', cardId, calledShape: calledShape || null },
    });
    setWaitingForShape(null);
  };

  const handleDrawCard = () => {
    if (!socketRef.current || !playId) return;
    socketRef.current.emit('tournamentAction', {
      playId,
      action: { type: 'drawCard' },
    });
  };

  const handleResign = () => setShowResignConfirm(true);
  const confirmResign = () => {
    setShowResignConfirm(false);
    socketRef.current?.emit('tournamentResign', { playId });
  };

  const handleShapeSelect = (shape) => {
    if (waitingForShape) {
      handlePlayCard(waitingForShape.cardId, shape);
    }
  };

  // ========================================================
  // UTILS
  // ========================================================
  const getUserIdFromToken = () => {
    try {
      const token = getAccessToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch { return null; }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#f59e0b', live: '#10b981', closed: '#6b7280',
      completed: '#3b82f6', cancelled: '#ef4444',
    };
    return colors[status] || '#aaa';
  };

  const getGameIcon = (type) => type === 'whot' ? '🃏' : '🎮';
  const getGameName = (type) => type === 'whot' ? 'Whot' : 'Archived tournament';

  const formatTimeLeft = (startTime, endTime, status) => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (status === 'scheduled' && start > now) {
      const diff = start - now;
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      return `Starts in ${hours > 0 ? `${hours}h ` : ''}${mins}m`;
    }
    if (status === 'live') {
      const diff = end - now;
      if (diff <= 0) return 'Ending soon';
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      return `${hours > 0 ? `${hours}h ` : ''}${mins}m remaining`;
    }
    return '';
  };

  // ========================================================
  // RENDER HELPERS
  // ========================================================
  const isLegacyTournament = selectedTournament && (selectedTournament.game_type || selectedTournament.gameType) !== 'whot';
  const actionsRemaining = gameState?.actionsRemaining || 0;
  const maxActions = gameState?.maxActions || 50;
  const missedTurns = gameState?.missedTurns || 0;

  // ========================================================
  // RENDER: WINNER MODAL
  // ========================================================
  if (winnerModal && winnerData) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: '#1a1a2e', borderRadius: '20px', padding: '40px', maxWidth: '400px',
          width: '90%', textAlign: 'center', border: '2px solid #f59e0b',
        }}>
          <p style={{ fontSize: '64px', margin: '0' }}>🏆</p>
          <h2 style={{ color: '#f59e0b', margin: '10px 0' }}>Congratulations!</h2>
          <p style={{ color: '#aaa', fontSize: '16px' }}>
            You placed <strong>#{winnerData.rank}</strong> in {selectedTournament?.name}!
          </p>
          <div style={{
            background: '#111122', borderRadius: '12px', padding: '20px', margin: '20px 0',
            border: '1px solid #f59e0b44',
          }}>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#00d4aa' }}>
              ₦{parseFloat(winnerData.amount || 0).toLocaleString()}
            </div>
            <div style={{ color: '#888', marginTop: '4px' }}>Prize Won</div>
          </div>
          <div style={{
            padding: '8px 16px', borderRadius: '6px', fontSize: '14px',
            background: winnerData.status === 'paid' ? '#10b98122' : '#f59e0b22',
            color: winnerData.status === 'paid' ? '#10b981' : '#f59e0b',
            display: 'inline-block', marginBottom: '20px',
          }}>
            {winnerData.status === 'paid' ? '✅ Paid' : winnerData.status === 'approved' ? '👀 Approved' : '⏳ Pending'}
          </div>
          <button onClick={() => { setWinnerModal(false); setWinnerData(null); }}
            style={{
              padding: '12px 32px', background: '#00d4aa', color: '#000',
              borderRadius: '10px', border: 'none', cursor: 'pointer',
              fontWeight: 'bold', fontSize: '16px', width: '100%',
            }}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ========================================================
  // RENDER: GAME PLAY MODE
  // ========================================================
  if (gameMode === 'connecting' || gameMode === 'playing' || gameMode === 'finished') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a0a1a', color: '#fff' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', background: '#1a1a2e', borderBottom: '1px solid #2a2a3e',
        }}>
          <div>
            <span style={{ color: '#888', fontSize: '13px' }}>{selectedTournament?.name}</span>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Score: <strong style={{ color: '#f59e0b' }}>
                {isLegacyTournament ? (gameState?.scores?.[Object.keys(gameState?.scores || {})[0]] || 0)
                        : (gameState?.players?.find(p => !p.isBot)?.score || 0)}
              </strong>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', fontSize: '14px' }}>
            <span>Plays: <strong>{actionsRemaining}/{maxActions}</strong></span>
            <span style={{
              padding: '4px 8px', borderRadius: '4px',
              background: turnTimer <= 3 ? '#ef444433' : '#2a2a3e',
              color: turnTimer <= 3 ? '#ef4444' : '#aaa',
            }}>
              ⏱ {turnTimer}s
            </span>
            <span style={{ color: missedTurns > 0 ? '#ef4444' : '#666', fontSize: '13px' }}>
              Misses: {missedTurns}/{MISS_LIMIT}
            </span>
          </div>
        </div>

        {/* Game area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px', overflow: 'auto' }}>
          {gameMode === 'connecting' && (
            <div style={{ textAlign: 'center', color: '#888' }}>
              <p style={{ fontSize: '32px' }}>🎮</p>
              <p>Connecting to game server...</p>
            </div>
          )}

          {gameMode === 'playing' && (
            isLegacyTournament ? (
              <div style={{padding:20,textAlign:'center',color:'#888'}}>Archived tournament gameplay is unavailable.</div>
            ) : (
              <WhotSoloUI
                gameState={gameState}
                waitingForShape={waitingForShape}
                onPlayCard={handlePlayCard}
                onDrawCard={handleDrawCard}
                onResign={handleResign}
                onShapeSelect={handleShapeSelect}
              />
            )
          )}

          {gameMode === 'finished' && (
            <div style={{ textAlign: 'center', maxWidth: '450px', width: '100%', background: '#1a1a2e', borderRadius: '16px', padding: '32px', border: '1px solid #2a2a3e' }}>
              <p style={{ fontSize: '64px', margin: '0' }}>
                {gameResult?.status === 'forfeited' || gameResult?.status === 'disqualified' ? '😔'
                  : gameResult?.rank && gameResult.rank <= 3 ? '🏆' : '✅'}
              </p>
              <h2 style={{ color: '#00d4aa', margin: '10px 0' }}>
                {gameResult?.status === 'forfeited' ? 'Play Forfeited'
                  : gameResult?.status === 'disqualified' ? 'Disqualified'
                  : 'Play Complete!'}
              </h2>

              <div style={{ background: '#111122', borderRadius: '12px', padding: '20px', margin: '20px 0' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#f59e0b' }}>
                  {gameResult?.score || 0}
                </div>
                <div style={{ color: '#888', fontSize: '14px', marginTop: '4px' }}>Your Score</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div style={{ background: '#111122', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#00d4aa' }}>{gameResult?.actionsUsed || 0}</div>
                  <div style={{ color: '#888', fontSize: '12px' }}>Actions Used</div>
                </div>
                <div style={{ background: '#111122', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3b82f6' }}>
                    {gameResult?.rank ? `#${gameResult.rank}` : '-'}
                  </div>
                  <div style={{ color: '#888', fontSize: '12px' }}>Rank</div>
                </div>
                <div style={{ background: '#111122', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{gameResult?.totalPlays || 0}</div>
                  <div style={{ color: '#888', fontSize: '12px' }}>Total Plays</div>
                </div>
                <div style={{ background: '#111122', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f59e0b' }}>
                    {playsRemaining !== null ? playsRemaining : '-'}
                  </div>
                  <div style={{ color: '#888', fontSize: '12px' }}>Plays Left</div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={() => {
                  setGameMode(null);
                  setPlayId(null);
                  setGameState(null);
                  setGameResult(null);
                  setDiceResult(null);
                  setValidMoves([]);
                  setWaitingForShape(null);
                }}
                  style={{ padding: '12px 24px', background: '#f59e0b', color: '#000', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                  {playsRemaining > 0 ? '▶ Play Again' : 'View Leaderboard'}
                </button>
                <button onClick={() => {
                  setGameMode(null);
                  setPlayId(null);
                  setGameState(null);
                  setGameResult(null);
                  setDiceResult(null);
                  setValidMoves([]);
                  setWaitingForShape(null);
                }}
                  style={{ padding: '12px 24px', background: '#2a2a3e', color: '#aaa', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                  {playsRemaining > 0 ? 'Back to Tournament' : 'Back'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================================
  // RENDER: TOURNAMENT DETAIL VIEW
  // ========================================================
  if (selectedTournament) {
    const t = selectedTournament;
    const entryFee = parseFloat(t.entry_fee || 0);
    const rankRewards = getRankRewards(t);
    const isLive = t.status === 'live';
    const canJoin = ['scheduled', 'live'].includes(t.status) && !isJoined && (t.current_players || 0) < t.max_players;
    const canPlay = isJoined && isLive && (playsRemaining > 0);

    return (
      <>
      {showResignConfirm && (
        <PlatformConfirmModal
          title="Resign Tournament Play?"
          message="Are you sure you want to resign? This will count as a forfeit."
          cancelLabel="Keep Playing"
          confirmLabel="Resign"
          onCancel={() => setShowResignConfirm(false)}
          onConfirm={confirmResign}
        />
      )}
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
        <button onClick={backToList}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '14px' }}>
          ← Back to Tournaments
        </button>

        {/* Header */}
        <div style={{
          background: '#1a1a2e', borderRadius: '12px', padding: '24px', marginTop: '16px',
          border: '1px solid #2a2a3e',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '32px' }}>{getGameIcon(t.game_type)}</span>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '22px' }}>{t.name}</h1>
              <span style={{ color: getStatusColor(t.status) }}>
                ● {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
              </span>
              {t.status === 'cancelled' && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#3b1111', border: '1px solid #7f1d1d', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '800', color: '#ef4444' }}>Tournament Cancelled</div>
                  <div style={{ color: '#ccc', marginTop: '6px' }}>Reason: {t.cancellation_reason || 'No reason provided.'}</div>
                  {isJoined && <div style={{ color: '#10b981', marginTop: '6px' }}>Entry Refund: Check your transaction history for the authoritative refund record.</div>}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {canJoin && (
                <button onClick={handleJoin} disabled={joining}
                  style={{
                    padding: '10px 24px', background: '#00d4aa', color: '#000',
                    borderRadius: '8px', border: 'none', cursor: 'pointer',
                    fontWeight: 'bold', fontSize: '15px',
                  }}>
                  {joining ? 'Joining...' : `Play ₦${entryFee.toLocaleString()}`}
                </button>
              )}
              {canPlay && (
                <button onClick={handleStartPlay}
                  style={{
                    padding: '10px 24px', background: '#f59e0b', color: '#000',
                    borderRadius: '8px', border: 'none', cursor: 'pointer',
                    fontWeight: 'bold', fontSize: '15px',
                  }}>
                  ▶ Play ({playsRemaining} left)
                </button>
              )}
              {isJoined && playsRemaining === 0 && (
                <div style={{ width: '100%', maxWidth: '520px', marginTop: '8px', padding: '14px', background: '#111122', border: '1px solid #2a2a3e', borderRadius: '10px' }}>
                  <div style={{ fontWeight: '800', color: '#f59e0b' }}>🏆 Tournament Plays Completed</div>
                  <div style={{ color: '#ccc', marginTop: '6px', lineHeight: 1.5 }}>You've used all {t.max_plays_per_player} of your {t.max_plays_per_player} available plays for this tournament.</div>
                  <div style={{ color: '#aaa', marginTop: '4px', lineHeight: 1.5 }}>Your results have been recorded on the leaderboard.</div>
                  <button type="button" onClick={() => setDetailTab('leaderboard')} style={{ marginTop: '10px', padding: '9px 14px', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}>View Leaderboard</button>
                  <button type="button" onClick={() => window.alert(`🏆 Tournament Plays Completed\n\nYou've used all ${t.max_plays_per_player} of your ${t.max_plays_per_player} available plays for this tournament.\n\nYour results have been recorded on the leaderboard.`)} style={{ marginTop: '10px', marginLeft: '8px', padding: '9px 14px', background: '#2a2a3e', color: '#aaa', border: '1px solid #444', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>🔒 Play Limit Reached</button>
                </div>
              )}
              {isJoined && isLive && playsRemaining === 1 && (
                <span style={{ color: '#f59e0b', padding: '10px 4px', fontWeight: '700' }}>
                  1 play remaining · {t.max_plays_per_player - 1} of {t.max_plays_per_player} plays used
                </span>
              )}
              {isJoined && !isLive && playsRemaining !== null && playsRemaining > 0 && (
                <span style={{ color: '#888', padding: '10px' }}>
                  {playsRemaining} plays remaining
                </span>
              )}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '24px', marginTop: '20px', flexWrap: 'wrap', fontSize: '14px', color: '#ccc' }}>
            <div>💰 Entry: <strong>₦{entryFee.toLocaleString()}</strong></div>
            <div>👥 Players: <strong>{t.current_players || 0}/{t.max_players}</strong></div>
            <div>🎮 Max Plays: <strong>{t.max_plays_per_player}</strong></div>
            <div>🎯 Actions/Game: <strong>{t.max_actions_per_game}</strong></div>
            {Number(t.advertised_reward_pool || 0) > 0 ? (
              <div>🏆 Pool: <strong style={{ color: '#f59e0b' }}>₦{getAdvertisedRewardPool(t).toLocaleString()}</strong></div>
            ) : null}
            <div>{t.payout_mode === 'auto' ? '🤖 Auto' : '👀 Review'}</div>
            <div style={{ color: '#f59e0b' }}>{formatTimeLeft(t.start_time, t.end_time, t.status)}</div>
          </div>

          <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
            Starts: {formatTournamentDateTime(t.start_time)} — Ends: {formatTournamentDateTime(t.end_time)}
          </div>

          {/* Private: Plays remaining */}
          {isJoined && (
            <div style={{
              marginTop: '12px', padding: '8px 12px',
              background: '#111122', borderRadius: '8px', fontSize: '13px',
              color: '#f59e0b', display: 'inline-block',
            }}>
              🎮 Plays remaining: {playsRemaining}/{t.max_plays_per_player}
            </div>
          )}
        </div>

        {/* Prize Distribution */}
        {t.prize_distribution && (() => {
          try {
            const dist = typeof t.prize_distribution === 'string' ? JSON.parse(t.prize_distribution) : t.prize_distribution;
            if (Array.isArray(dist) && dist.length > 0) {
              return (
                <div style={{
                  background: '#1a1a2e', borderRadius: '12px', padding: '16px',
                  marginTop: '12px', border: '1px solid #2a2a3e',
                }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>🏆 Prize Distribution</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {dist.map((d, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '8px 12px', background: '#111122', borderRadius: '6px',
                      }}>
                        <span style={{ color: '#ccc' }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}{' '}
                          {d.label || `Rank ${i + 1}`}
                        </span>
                        <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                          ₦{Number(rankRewards.find(r => Number(r.rank) === Number(d.rank))?.amount || d.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
          } catch (e) { /* ignore */ }
          return null;
        })()}

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '8px', marginTop: '20px',
          borderBottom: '1px solid #2a2a3e', paddingBottom: '10px',
        }}>
          {['leaderboard', 'payouts'].map(tabName => (
            <button key={tabName} onClick={() => setDetailTab(tabName)}
              style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: detailTab === tabName ? '#00d4aa' : 'transparent',
                color: detailTab === tabName ? '#000' : '#888',
                cursor: 'pointer', fontWeight: detailTab === tabName ? 'bold' : 'normal',
              }}>
              {tabName.charAt(0).toUpperCase() + tabName.slice(1)}
              {tabName === 'leaderboard' && leaderboard.length > 0 && ` (${leaderboard.length})`}
            </button>
          ))}
        </div>

        {/* Leaderboard */}
        {detailTab === 'leaderboard' && (
          <div style={{ marginTop: '12px' }}>
            {!isJoined && t.status === 'live' ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                Join the tournament to see the leaderboard!
              </p>
            ) : leaderboard.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                {t.status === 'live' ? 'No completed plays yet. Be the first!' : 'No plays recorded.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {leaderboard.map((entry, idx) => {
                  const userId = getUserIdFromToken();
                  const isMe = userId && entry.user_id === userId;
                  return (
                    <div key={entry.play_id || idx} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      background: isMe ? '#1a2a2e' : '#1a1a2e',
                      borderRadius: '8px', padding: '12px 16px',
                      border: isMe ? '1px solid #00d4aa44' : '1px solid #2a2a3e',
                    }}>
                      <span style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        background: entry.rank <= 3 ? '#f59e0b' : '#2a2a3e',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', fontSize: '14px',
                        color: entry.rank <= 3 ? '#000' : '#888',
                      }}>
                        {entry.rank}
                      </span>
                      <span style={{ flex: 1, fontWeight: isMe ? 'bold' : 'normal' }}>
                        {entry.username || `Player #${entry.user_id}`}
                        {isMe && ' (You)'}
                      </span>
                      <span style={{ color: '#00d4aa', fontWeight: 'bold', fontSize: '16px' }}>
                        {entry.score}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Payouts */}
        {detailTab === 'payouts' && (
          <div style={{ marginTop: '12px' }}>
            {t.status !== 'completed' ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                Payouts will be available when the tournament is completed.
              </p>
            ) : payouts.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                No payouts have been processed yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {payouts.map(p => (
                  <div key={p.id} style={{
                    background: '#1a1a2e', borderRadius: '8px', padding: '12px 16px',
                    border: '1px solid #2a2a3e',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <span style={{ fontWeight: 'bold' }}>#{p.rank}</span>
                      <span style={{ marginLeft: '8px' }}>{p.username || `User #${p.user_id}`}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        ₦{parseFloat(p.amount).toLocaleString()}
                      </span>
                      <span style={{
                        padding: '4px 8px', borderRadius: '4px', fontSize: '12px',
                        background: p.status === 'paid' ? '#10b98133' : '#f59e0b33',
                        color: p.status === 'paid' ? '#10b981' : '#f59e0b',
                      }}>
                        {p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </>
    );
  }

  // ========================================================
  // RENDER: TOURNAMENT LIST
  // ========================================================
  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <Link to="/dashboard" style={{ color: '#888', textDecoration: 'none', fontSize: '14px' }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ margin: '10px 0 0' }}>🏆 Tournaments</h1>
        </div>
        {isLoggedIn && myTournaments.length > 0 && (
          <span style={{ color: '#00d4aa', fontSize: '14px', background: '#1a1a2e', padding: '8px 16px', borderRadius: '8px' }}>
            My Tournaments: {myTournaments.length}
          </span>
        )}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '10px', margin: '20px 0', flexWrap: 'wrap' }}>
        {['all', 'scheduled', 'live', 'completed'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px', borderRadius: '20px', border: '1px solid #333',
              background: filter === f ? '#00d4aa' : 'transparent',
              color: filter === f ? '#000' : '#aaa',
              cursor: 'pointer', fontSize: '14px',
              fontWeight: filter === f ? 'bold' : 'normal',
            }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#3b1111', color: '#ef4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <Loading variant="section" label="" />
      ) : tournaments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          <p style={{ fontSize: '48px', margin: '0' }}>🏆</p>
          <p>No tournaments found.</p>
          {isLoggedIn && <p style={{ fontSize: '14px' }}>Check back later or ask an admin to create one!</p>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {tournaments.map(t => {
            const isJoinedTourn = myTournaments.some(mt => mt.id === t.id);
            const entryFee = parseFloat(t.entry_fee || 0);
            const timeLeft = formatTimeLeft(t.start_time, t.end_time, t.status);
            const advertisedRewardPool = getAdvertisedRewardPool(t);
            const rankRewards = getRankRewards(t);

            return (
              <div key={t.id}
                onClick={() => viewTournament(t)}
                style={{
                  background: '#1a1a2e', borderRadius: '12px', padding: '20px',
                  border: '1px solid #2a2a3e', cursor: 'pointer',
                  transition: 'border-color 0.2s, transform 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#00d4aa66'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a3e'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '24px' }}>{getGameIcon(t.game_type)}</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px' }}>{t.name}</h3>
                        <span style={{ color: '#888', fontSize: '13px' }}>
                          {getGameName(t.game_type)} • {t.max_plays_per_player} plays/player
                        </span>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
                      <span>💰 <strong>₦{entryFee.toLocaleString()}</strong></span>
                      <span>👥 <strong>{t.current_players || 0}/{t.max_players}</strong></span>
                      <span style={{ color: getStatusColor(t.status) }}>
                        ● {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                      </span>
                      {timeLeft && <span>⏰ {timeLeft}</span>}
                    </div>
                    {Number(t.advertised_reward_pool || 0) > 0 ? (
                      <div style={{ marginTop: '8px', fontSize: '13px' }}>
                        🏆 First Prize Pool: <strong style={{ color: '#f59e0b' }}>₦{advertisedRewardPool.toLocaleString()}</strong>
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                    {isJoinedTourn && (
                      <span style={{
                        padding: '4px 12px', background: '#00d4aa22', color: '#00d4aa',
                        borderRadius: '6px', fontSize: '12px', fontWeight: 'bold',
                      }}>
                        ✓ Joined
                      </span>
                    )}
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      {new Date(t.start_time).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
